import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { MobXProviderContext } from "mobx-react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  publicMutedTextStyle,
  publicPageContainerStyle,
  publicSectionCardStyle,
} from "../../../components/general/publicPageStyles";
import { getErrorTitle } from "../../../constants/constants";
import { sanitizeRichHtml } from "../../../utils/securityUtils";

const BookDemoPage = () => {
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const toast = useRef<Toast>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [slots, setSlots] = useState<any[]>([]);
  const [selectedSlotId, setSelectedSlotId] = useState<string>("");
  const [showSignupDialog, setShowSignupDialog] = useState(false);
  const [showGoogleSchedulerDialog, setShowGoogleSchedulerDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [cancelMessage, setCancelMessage] = useState("");
  const [form, setForm] = useState({
    fullName: "",
    email: "",
  });
  const requestedSlotId = String(searchParams.get("slotId") || "").trim();

  const selectedSlot = useMemo(
    () => (slots || []).find((slot: any) => String(slot?.slotId || "") === selectedSlotId) || null,
    [selectedSlotId, slots],
  );

  const loadDemoSlots = async () => {
    setLoading(true);
    try {
      const response = await backendAPIStore.getPublicDemoSlots();
      const nextSlots = Array.isArray(response?.slots) ? response.slots : [];
      setSlots(nextSlots);
      setSelectedSlotId((current) => {
        if (requestedSlotId && nextSlots.some((slot: any) => slot.slotId === requestedSlotId)) {
          return requestedSlotId;
        }
        if (current && nextSlots.some((slot: any) => slot.slotId === current)) {
          return current;
        }
        return String(nextSlots?.[0]?.slotId || "");
      });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error.message, life: 3500 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadDemoSlots();
  }, [requestedSlotId]);

  useEffect(() => {
    let isMounted = true;
    const hydrateSignedInUser = async () => {
      try {
        const authenticated = await authStore.isUserLoggedIn();
        if (!authenticated) {
          return;
        }
        await backendAPIStore.init();
        const userId = authStore.getUserId();
        if (!userId) {
          return;
        }
        const profile = await backendAPIStore.getUserProfile(userId);
        if (!isMounted) {
          return;
        }
        setForm((current) => ({
          fullName: current.fullName || `${String(profile?.firstName || "").trim()} ${String(profile?.lastName || "").trim()}`.trim(),
          email: current.email || String(profile?.email || "").trim(),
        }));
      } catch {
        // keep public flow lightweight
      }
    };

    void hydrateSignedInUser();
    return () => {
      isMounted = false;
    };
  }, [authStore, backendAPIStore]);

  useEffect(() => {
    if (requestedSlotId && selectedSlotId === requestedSlotId && selectedSlot) {
      setShowSignupDialog(true);
    }
  }, [requestedSlotId, selectedSlot, selectedSlotId]);

  useEffect(() => {
    const cancelToken = String(searchParams.get("cancelToken") || "").trim();
    if (!cancelToken) {
      return;
    }

    const cancelRegistration = async () => {
      setCancelling(true);
      try {
        await backendAPIStore.cancelPublicDemoRegistration(cancelToken);
        setCancelMessage("Your demo registration has been cancelled.");
        setSearchParams((currentParams) => {
          const next = new URLSearchParams(currentParams);
          next.delete("cancelToken");
          return next;
        }, { replace: true });
        await loadDemoSlots();
      } catch (error: any) {
        toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error.message, life: 3500 });
      } finally {
        setCancelling(false);
      }
    };

    void cancelRegistration();
  }, [backendAPIStore, searchParams, setSearchParams]);

  const openSignupDialog = (slotId: string) => {
    setSelectedSlotId(slotId);
    setShowSignupDialog(true);
  };

  const onSubmit = async () => {
    if (!selectedSlotId || !form.fullName.trim() || !form.email.trim()) {
      toast.current?.show({ severity: "warn", summary: "Missing details", detail: "Please choose a demo and enter your name and email.", life: 3000 });
      return;
    }
    setSubmitting(true);
    setSuccessMessage("");
    try {
      const response = await backendAPIStore.registerPublicDemo({
        slotId: selectedSlotId,
        fullName: form.fullName.trim(),
        email: form.email.trim(),
      });
      setSuccessMessage(`You're booked for ${response?.dateTimeLabel || "the selected demo slot"}. A confirmation email has been sent to ${form.email.trim()}.`);
      setShowSignupDialog(false);
      await loadDemoSlots();
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error.message, life: 3500 });
    } finally {
      setSubmitting(false);
    }
  };

  const renderDemoDescriptionHtml = (htmlContent?: string) => {
    const normalizedHtml = String(htmlContent || "").trim();
    if (!normalizedHtml) {
      return null;
    }

    return (
      <div
        style={{
          ...publicMutedTextStyle,
          fontSize: "0.95rem",
          lineHeight: 1.7,
        }}
        dangerouslySetInnerHTML={{
          __html: sanitizeRichHtml(normalizedHtml).replace(/\n/g, "<br />"),
        }}
      />
    );
  };

  return (
    <div style={publicPageContainerStyle}>
      <Toast ref={toast} />

      <div
        style={{
          marginBottom: "1rem",
          borderRadius: "18px",
          border: "1px solid #d9e8f5",
          background: "#ffffff",
          padding: "1rem 1.1rem",
          boxShadow: "0 14px 28px rgba(15, 61, 99, 0.06)",
        }}
      >
        <div className="flex align-items-center justify-content-center gap-3 flex-wrap">
          <div style={{ color: "#0f3d63", fontWeight: 800, fontSize: "1.05rem", textAlign: "center" }}>Book a private demo</div>
          <div style={{ ...publicMutedTextStyle, fontSize: "0.9rem", textAlign: "center" }}>Choose a time that works for you and meet with our team one-on-one.</div>
          <Button
            type="button"
            label="Book Private Demo"
            icon="pi pi-calendar-plus"
            severity="warning"
            rounded
            onClick={() => setShowGoogleSchedulerDialog(true)}
          />
        </div>
      </div>

      {cancelling ? (
        <div className="flex justify-content-center py-6">
          <ProgressSpinner style={{ width: "52px", height: "52px" }} strokeWidth="4" />
        </div>
      ) : null}

      {cancelMessage ? <Message severity="success" text={cancelMessage} className="w-full mb-3" /> : null}
      {successMessage ? <Message severity="success" text={successMessage} className="w-full mb-3" /> : null}

      {!loading && (slots || []).length > 0 ? (
        <div style={{ margin: "1.25rem 0 0.85rem", color: "#0f3d63", textAlign: "center" }}>
          <div style={{ fontWeight: 800, fontSize: "1.15rem" }}>Attend a public group demo</div>
          <div style={{ ...publicMutedTextStyle, marginTop: "0.25rem" }}>Join one of the group demo sessions scheduled by our team.</div>
        </div>
      ) : null}

      {loading ? (
              <div className="flex justify-content-center py-6">
                <ProgressSpinner style={{ width: "56px", height: "56px" }} strokeWidth="4" />
              </div>
            ) : (slots || []).length === 0 ? (
              <div className="flex flex-column align-items-center text-center py-4">
                <div
                  className="inline-flex align-items-center justify-content-center mb-3"
                  style={{
                    width: "3.5rem",
                    height: "3.5rem",
                    borderRadius: "999px",
                    background: "#fff7ed",
                    color: "#c76b11",
                  }}
                >
                  <i className="pi pi-calendar-times" style={{ fontSize: "1.35rem" }} />
                </div>
                <div style={{ color: "#0f3d63", fontWeight: 800, fontSize: "1.15rem", marginBottom: "0.5rem" }}>
                  No public group demos scheduled right now
                </div>
                <div style={{ ...publicMutedTextStyle, maxWidth: "34rem" }}>
                  There are no public group demo sessions scheduled at the moment. You can still book a private demo above.
                </div>
              </div>
            ) : (
              <div className="grid justify-content-center">
                {(slots || []).map((slot: any) => (
                  <div className="col-12 md:col-6 xl:col-4" key={slot.slotId}>
                    <Card style={{ ...publicSectionCardStyle, height: "100%", textAlign: "center" }}>
                      <div className="flex flex-column gap-3" style={{ height: "100%" }}>
                        <div>
                          <div style={{ color: "#0f3d63", fontWeight: 800, fontSize: "1.05rem", lineHeight: 1.35 }}>
                            {slot.demoTitle || "General Product Demo"}
                          </div>
                          <div style={{ ...publicMutedTextStyle, marginTop: "0.35rem" }}>{slot.dateTimeLabel}</div>
                        </div>

                        {Array.isArray(slot.selectedFeatures) && slot.selectedFeatures.length > 0 ? (
                          <div>
                            <div style={{ color: "#0f3d63", fontWeight: 700, marginBottom: "0.45rem" }}>Topics</div>
                            <div className="flex flex-wrap justify-content-center gap-2">
                              {slot.selectedFeatures.slice(0, 4).map((feature: string) => (
                                <span
                                  key={`${slot.slotId}-${feature}`}
                                  className="inline-flex align-items-center px-3 py-2 border-round-2xl"
                                  style={{ background: "#ffffff", border: "1px solid #d9e8f5", color: "#185da2", fontWeight: 600, fontSize: "0.88rem" }}
                                >
                                  {feature}
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : null}

                        {renderDemoDescriptionHtml(slot.customFeatureNotes)}

                        <div className="mt-auto flex flex-wrap justify-content-center gap-2">
                          <Button
                            label="Signup"
                            icon="pi pi-user-plus"
                            severity="warning"
                            rounded
                            onClick={() => openSignupDialog(String(slot.slotId || ""))}
                          />
                          {String(slot.meetingLink || "").trim() ? (
                            <Button
                              label="Join Link"
                              icon="pi pi-external-link"
                              severity="secondary"
                              outlined
                              rounded
                              onClick={() => window.open(String(slot.meetingLink || "").trim(), "_blank", "noopener,noreferrer")}
                            />
                          ) : null}
                        </div>
                      </div>
                    </Card>
                  </div>
                ))}
              </div>
      )}

      <Dialog
        header={selectedSlot?.demoTitle || "Signup for Demo"}
        visible={showSignupDialog}
        modal
        style={{ width: "min(92vw, 32rem)" }}
        onHide={() => !submitting && setShowSignupDialog(false)}
      >
        <div className="flex flex-column gap-3">
          <div>
            <label htmlFor="demo-full-name" style={{ display: "block", color: "#0f3d63", fontWeight: 700, marginBottom: "0.45rem" }}>Full name</label>
            <InputText
              id="demo-full-name"
              value={form.fullName}
              onChange={(e) => setForm((current) => ({ ...current, fullName: e.target.value }))}
              className="w-full"
              placeholder="Your full name"
            />
          </div>
          <div>
            <label htmlFor="demo-email" style={{ display: "block", color: "#0f3d63", fontWeight: 700, marginBottom: "0.45rem" }}>Email address</label>
            <InputText
              id="demo-email"
              value={form.email}
              onChange={(e) => setForm((current) => ({ ...current, email: e.target.value }))}
              className="w-full"
              placeholder="name@example.com"
            />
          </div>
          <Button
            label={submitting ? "Booking..." : "Book Demo"}
            severity="warning"
            rounded
            icon="pi pi-calendar-plus"
            onClick={() => void onSubmit()}
            loading={submitting}
            disabled={!selectedSlotId}
            style={{ width: "50%" }}
          ></Button>
          <div style={{ ...publicMutedTextStyle, fontSize: "0.92rem" }}>
            We&apos;ll email your confirmation right away. If you need to cancel, use the link in that email before the session begins.
          </div>
        </div>
      </Dialog>
      <Dialog
        header="Book a private demo"
        visible={showGoogleSchedulerDialog}
        modal
        style={{ width: "min(96vw, 62rem)" }}
        onHide={() => setShowGoogleSchedulerDialog(false)}
      >
        <iframe
          title="Schedule an AFH Hub demo"
          src="https://calendar.google.com/calendar/appointments/schedules/AcZssZ2KqaYX3zpNXL8KxBXNlfeqsivP4BkCmnfsr0_BVlDc3j4sn1OLh7_L863s_064P3PCL7ec0jTE?gv=true"
          style={{ border: 0, display: "block", width: "100%", height: "600px" }}
          frameBorder="0"
        />
      </Dialog>
    </div>
  );
};

export default BookDemoPage;
